import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AgeStanding } from "@/engine/state/ageFloor";
import { useHydrated } from "@/hooks/useHydrated";

export interface AgeQuestionProps {
  standing: AgeStanding;
  /** Whether the door is being opened now; a door nobody pressed asks nothing. */
  open: boolean;
  onYes: () => void;
  onNo: () => void;
}

/**
 * The question every door asks once (`age-floor`), or the refusal in its place.
 *
 * It cannot be dismissed: closing it would leave the door shut with nothing on
 * screen saying why. The close cross the library draws is hidden for the same
 * reason, and Escape and a press outside do nothing.
 */
export function AgeQuestion({ standing, open, onYes, onNo }: AgeQuestionProps) {
  const { t } = useTranslation();
  // Until the page has hydrated the browser's answer has not been read, and a
  // returning visitor would see the question flash.
  const hydrated = useHydrated();
  if (standing === "confirmed") return null;
  const refused = standing === "refused";
  return (
    <Dialog open={open && hydrated}>
      <DialogContent
        data-testid={refused ? "age-refused" : "age-question"}
        className="max-w-sm [&>button:last-child]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {refused ? t("age.refusedTitle") : t("age.title")}
          </DialogTitle>
          <DialogDescription>
            {refused ? t("age.refused") : t("age.question")}
          </DialogDescription>
        </DialogHeader>
        {refused ? null : (
          <DialogFooter className="gap-2 sm:justify-center">
            <Button data-testid="age-yes" onClick={onYes}>
              {t("age.yes")}
            </Button>
            <Button data-testid="age-no" variant="outline" onClick={onNo}>
              {t("age.no")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
